import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/hooks/useTenant';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { CreateBlockedPeriodData } from '@/lib/schemas/blockedPeriods';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ChatMessage, 
  ChatSuggestion, 
  ChatbotContextData 
} from '@/lib/schemas/chatbot';

export const useChatbot = () => {
  const { t } = useTranslation();
  const { currentTenant } = useTenant();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  // Mutation for creating blocked periods
  const createBlockedPeriod = useMutation({
    mutationFn: async (data: CreateBlockedPeriodData) => {
      if (!currentTenant?.id) throw new Error('No tenant selected');

      const { data: newPeriod, error } = await supabase
        .from('blocked_periods')
        .insert({
          ...data,
          tenant_id: currentTenant.id,
        })
        .select()
        .single();

      if (error) throw error;
      return newPeriod;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blocked-periods'] });
    },
  });

  const toggleChatbot = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const addMessage = useCallback((message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const newMessage: ChatMessage = {
      ...message,
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage;
  }, []);

  const sendMessage = useCallback(async (
    message: string,
    contextData?: ChatbotContextData
  ): Promise<ChatMessage | undefined> => {
    if (!currentTenant?.id || !user || !message.trim()) {
      return undefined;
    }

    // Add user message
    addMessage({
      role: 'user',
      content: message.trim(),
    });

    setIsLoading(true);

    try {
      console.log('[CHATBOT] Starting message send process...');
      
      // Get the current user's JWT token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        console.error('[CHATBOT] Authentication error:', sessionError);
        throw new Error('Authentication required');
      }
      console.log('[CHATBOT] Authentication successful');

      // Prepare conversation history for API
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content,
        suggestions: msg.suggestions
      }));

      const requestPayload = {
        tenant_id: currentTenant.id,
        message: message.trim(),
        session_id: sessionId,
        conversation_history: conversationHistory,
        action_context: contextData
      };

      console.log('[CHATBOT] Calling chatbot-admin function with payload:', {
        tenant_id: currentTenant.id,
        message_length: message.trim().length,
        history_length: conversationHistory.length,
        has_context: !!contextData
      });

      // Call the chatbot-admin edge function
      const { data, error } = await supabase.functions.invoke('chatbot-admin', {
        body: requestPayload,
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      console.log('[CHATBOT] Function call completed, error:', !!error, 'data:', !!data);

      if (error) {
        console.error('Chatbot API error:', error);
        throw new Error(error.message || 'Failed to get response from AI assistant');
      }

      // Add assistant response
      // Ensure all suggestions have a status defaulted to 'pending'
      const suggestions = data.suggestions || (data.suggestion ? [data.suggestion] : undefined);
      const suggestionsWithStatus = suggestions?.map((s: ChatSuggestion) => ({
        ...s,
        status: s.status || 'pending'
      }));

      const assistantMessage = addMessage({
        role: 'assistant',
        content: data.reply || 'I apologize, but I couldn\'t process your request.',
        suggestions: suggestionsWithStatus,
      });

      return assistantMessage;

    } catch (error) {
      console.error('Failed to send message to chatbot:', error);
      
      addMessage({
        role: 'assistant',
        content: t('chatbotError'),
      });

      return undefined;
    } finally {
      setIsLoading(false);
    }
  }, [currentTenant?.id, user, messages, sessionId, addMessage, t]);

  // Helper function to update suggestion state
  const updateSuggestionState = useCallback((suggestion: ChatSuggestion, newStatus: 'accepted' | 'declined') => {
    setMessages(prev => prev.map(message => {
      if (message.suggestions) {
        const updatedSuggestions = message.suggestions.map(s => 
          s === suggestion ? { ...s, status: newStatus } : s
        );
        return { ...message, suggestions: updatedSuggestions };
      }
      return message;
    }));
  }, []);

  const acceptSuggestion = useCallback(async (suggestion: ChatSuggestion) => {
    if (!suggestion || suggestion.suggestion_type !== 'block_time') {
      return;
    }

    try {
      setIsLoading(true);

      // Create the blocked period data
      const blockedPeriodData: CreateBlockedPeriodData = {
        title: suggestion.reason || null, // Only set title if there's a reason
        description: t('chatbotBlockedViaAI'),
        start_time: suggestion.start_time!,
        end_time: suggestion.end_time!,
        period_type: 'unavailable', // Default to unavailable
        applies_to: suggestion.team_member_ids && suggestion.team_member_ids.length > 0 
          ? 'specific_team_members' 
          : 'all',
        team_member_ids: suggestion.team_member_ids && suggestion.team_member_ids.length > 0 
          ? suggestion.team_member_ids 
          : undefined,
      };

      // Call the API to create the blocked period
      await createBlockedPeriod.mutateAsync(blockedPeriodData);
      
      // Update suggestion state to accepted instead of adding a message
      updateSuggestionState(suggestion, 'accepted');

    } catch (error) {
      console.error('Failed to execute suggestion:', error);
      addMessage({
        role: 'assistant',
        content: `❌ ${t('chatbotBlockTimeError')}: ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsLoading(false);
    }
  }, [t, createBlockedPeriod, updateSuggestionState, addMessage]);

  const declineSuggestion = useCallback((suggestion: ChatSuggestion) => {
    // Update suggestion state to declined instead of adding a message
    updateSuggestionState(suggestion, 'declined');
  }, [updateSuggestionState]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return {
    // State
    isOpen,
    messages,
    isLoading,
    
    // Actions
    toggleChatbot,
    sendMessage,
    acceptSuggestion,
    declineSuggestion,
    clearMessages,
  };
};